import { Component, OnInit, OnDestroy } from '@angular/core';
import { Project } from 'src/app/models/project.model';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { Region } from '../../models/region.model';
import { Feature } from '../../models/feature.model';
import { animate, style, transition, trigger } from '@angular/animations';
import { AuthService } from 'src/app/services/auth.service';
import { RequestService } from 'src/app/services/request.service';
import { FeatureEnum } from 'src/app/models/enums/FeatureEnum';
import { TransportMode } from 'src/app/models/transportMode.model';
import { Default } from 'src/app/models/default.model';
import { PersonValueFormat } from 'src/app/models/enums/PersonValueFormatEnum';
import { TransportModeEnum } from 'src/app/models/enums/TransportModeEnum';
import { ComfortType } from 'src/app/models/comfort.mode';
import { ComfortTypeEnum } from 'src/app/models/enums/ComfortTypeEnum';
import { TransportModeDefault } from 'src/app/models/TransportModeDefault';
import { MatDialog } from '@angular/material/dialog';
import { AgeGenderDistribution } from 'src/app/models/AgeGenderDistribution.model';
import { CloneService } from 'src/app/services/clone.service';
import { AgeGenderCompareDialog } from 'src/app/Modal/ageGenderCompare.model';
import { SortAgeGenderDistributionValuesById } from 'src/app/helper/sortAgeGenderDistributionValuesById';
import { ModalDialog } from 'src/app/Modal/modal';
import { WaitForModalResult } from 'src/app/helper/modalHelper';
import { userHasFeature } from 'src/app/helper/userHasFeature';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';

@Component({
  selector: 'app-project-edit',
  templateUrl: './project-edit.component.html',
  styleUrls: ['./project-edit.component.css'],
  animations: [
    trigger(
      'enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('500ms', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('500ms', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ]
    )
  ]
})

export class ProjectEditComponent implements OnInit, OnDestroy {
  private unscubscribe$ = new Subject<void>();
  private formSubscription: Subscription | null = null;
  panelOpenState: boolean = false;
  projectId: number;
  currentProject: Project;
  transportModes: TransportMode[] = [];
  regions: Region[] = [];
  selectedProject;
  selectedTransportMode: TransportMode;
  selectedRegion: Region;
  selectedRegionTransportModeDefault: TransportModeDefault;
  projectForm;
  featureEnum = FeatureEnum;
  defOccupancyRateSelected: boolean;
  defStandingPlaceOccupancyRateSelected: boolean;
  isToolboxOverlay = false;
  featureList: Feature[] = [];
  comfortValues: ComfortType[] = [];
  currentAgeGenderDistribution: AgeGenderDistribution = null;
  ageGenderDistributionFromRegion: AgeGenderDistribution;
  backButtonModalMessage: string = "Please verify that you have saved the project, otherwise the unsaved changes will be lost!";
  showReservationNeeded: boolean;
  skipCanDeactivateOnSave: boolean = false;

  project = {
    addTitel: 'Add New Project',
    editTitel: 'Edit Project',
    chooseOtherProjectDescription: 'Edit an existing project:',
    projectSelectionLabel: 'Choose project',
    projectNameLabel: 'Project name',
    projectDescriptionLabel: 'Project description',
    transportModeLabel: 'Mode of transport',
    regionLabel: 'Region/Operator',
    checkBoxSeatReservation: 'Seat reservation is obligatory for the train',
    comfortLabel: 'Baggage lifting willingness',
    standingPlaceOccupancyRateLabel: 'Varying degree of standing-room utilisation (value in %)',
    personsPerSquareMeterEntranceArea: 'Persons/m² in entrance area',
    personsPerSquareMeterMultiPurposeArea: 'Persons/m² in multi-purpose area',
    personsPerSquareMeterAisle: 'Persons/m² in aisle',
    seatOccupancyrateLabel: 'Varying total seat occupancy rate (value in %)',
    personsBoardingLabel: 'Persons boarding',
    personsDeboardingLabel: 'Persons deboarding',
    absoluteLabel: 'Absolute',
    percentLabel: 'Percent',
    buttonConfigureToolbox: 'Configuration Of Toolbox',
    settingsTitel: 'Advanced Settings',
    settingsDescription: '',
    buttonSave: 'Save',
    buttonBack: 'Back',
    validation: {
      required: 'This field is required!',
      mLength100: 'Maximum Length Of 100 Characters Exeeded!',
      mLength255: 'Maximum Length Of 255 Characters Exeeded!',
      minValue1: "The Minimum Value Can't Be Lower Than 1!",
      maxValue100: "The Maximum Value Can't Be Greater Than 100!"
    }
  };

  // Ctor
  constructor(
    private router: Router, private route: ActivatedRoute, private requestService: RequestService, private cloneService: CloneService, private authService: AuthService, private dialog: MatDialog) {
  }

  // Lifecycle hooks
  ngOnInit() {
    this.route.params
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(params => {
        this.projectId = parseInt(params['id'].toString());
        this.initForm();

        this.requestService.getMasterData()
          .pipe(takeUntil(this.unscubscribe$))
          .subscribe(masterData => {

            this.transportModes = masterData.transportModeList;
            this.transportModes.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));

            this.comfortValues = masterData.comfortTypeList;
            this.comfortValues.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
          });

        this.requestService.getCompanyRegions(this.authService.getCurrentCompanyId())
          .pipe(takeUntil(this.unscubscribe$))
          .subscribe(res => {
            this.regions = res;
            this.regions.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));

            this.enableFeatures();
            if (this.projectId > 0) {
              this.loadproject();
            }
            else {
              this.enableRegionCtrl();
            }
          });
      });
  };

  // Methods
  initForm() {
    this.selectedProject = this.project.projectSelectionLabel;
    let currentUserId = this.authService.getCurrentUserId();
    let currentCompanyId = this.authService.getCurrentCompanyId();

    this.projectForm = new FormGroup({
      id: new FormControl(this.projectId),
      name: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
      description: new FormControl(null, Validators.maxLength(255)),
      companyId: new FormControl(currentCompanyId),
      transportModeId: new FormControl(null, Validators.required),
      regionId: new FormControl(null, Validators.required),
      createdBy: new FormControl(currentUserId),
      ageGenderDistribution: new FormControl(null),
      ageGenderDistributionId: new FormControl(null),
      lastModifiedBy: new FormControl(currentUserId),
      reservationNeeded: new FormControl(false),
      defaultId: new FormControl(null),
      default: new FormGroup({
        id: new FormControl(-1),
        seatOccupancyRateDefault: new FormControl(null),
        standingOccupancyRateDefault: new FormControl(null),
        personsBoardingDefault: new FormControl(null),
        personsDeboardingDefault: new FormControl(null),
        personValueFormatId: new FormControl(PersonValueFormat.Percent),
        personsPerSquareMeterEntranceArea: new FormControl(null),
        personsPerSquareMeterMultiPurposeArea: new FormControl(null),
        personsPerSquareMeterAisle: new FormControl(null)
      }),
      comfortTypeId: new FormControl(ComfortTypeEnum.Default)
    });
  }

  enableFeatures() {
    this.showReservationNeeded = userHasFeature(this.authService.getCurrentFeatures(), FeatureEnum.CanShowReservationNeededAddProject);
  }

  updateForm() {
    if (this.currentProject != null) {
      // set the current projectid as selected project
      this.selectedProject = this.currentProject.id;
      // get the current user id
      let currentUserId = this.authService.getCurrentUserId();
      let def = new Default();

      // set the default object
      if (this.currentProject.default != null) {
        def.id = this.currentProject.default.id;
        def.seatOccupancyRateDefault = this.currentProject.default.seatOccupancyRateDefault;
        def.standingOccupancyRateDefault = this.currentProject.default.standingOccupancyRateDefault;
        def.personsBoardingDefault = this.currentProject.default.personsBoardingDefault;
        def.personsDeboardingDefault = this.currentProject.default.personsDeboardingDefault;
        def.personValueFormatId = this.currentProject.default.personValueFormatId;
        def.personsPerSquareMeterEntranceArea = this.currentProject.default.personsPerSquareMeterEntranceArea;
        def.personsPerSquareMeterMultiPurposeArea = this.currentProject.default.personsPerSquareMeterMultiPurposeArea;
        def.personsPerSquareMeterAisle = this.currentProject.default.personsPerSquareMeterAisle;
      }
      else {
        def.id = -1;
        def.seatOccupancyRateDefault = null;
        def.standingOccupancyRateDefault = null;
        def.personsBoardingDefault = null;
        def.personsDeboardingDefault = null;
        def.personValueFormatId = PersonValueFormat.Percent;
        def.personsPerSquareMeterEntranceArea = null;
        def.personsPerSquareMeterMultiPurposeArea = null;
        def.personsPerSquareMeterAisle = null;
      }

      // add the controls to the form group
      this.projectForm = new FormGroup({
        id: new FormControl(this.currentProject.id),
        name: new FormControl(this.currentProject.name, [Validators.required, Validators.maxLength(100)]),
        description: new FormControl(this.currentProject.description, Validators.maxLength(255)),
        companyId: new FormControl(this.currentProject.companyId),
        transportModeId: new FormControl(this.currentProject.transportModeId, Validators.required),
        regionId: new FormControl(this.currentProject.regionId, Validators.required),
        createdBy: new FormControl(this.currentProject.createdBy),
        ageGenderDistribution: new FormControl(null),
        ageGenderDistributionId: new FormControl(null),
        lastModifiedBy: new FormControl(currentUserId),
        reservationNeeded: new FormControl(this.currentProject.reservationNeeded),
        defaultId: new FormControl(this.currentProject.defaultId),
        default: new FormGroup({
          id: new FormControl(def.id),
          seatOccupancyRateDefault: new FormControl(def.seatOccupancyRateDefault),
          standingOccupancyRateDefault: new FormControl(def.standingOccupancyRateDefault),
          personsBoardingDefault: new FormControl(def.personsBoardingDefault),
          personsDeboardingDefault: new FormControl(def.personsDeboardingDefault),
          personValueFormatId: new FormControl(def.personValueFormatId),
          personsPerSquareMeterEntranceArea: new FormControl(def.personsPerSquareMeterEntranceArea),
          personsPerSquareMeterMultiPurposeArea: new FormControl(def.personsPerSquareMeterMultiPurposeArea),
          personsPerSquareMeterAisle: new FormControl(def.personsPerSquareMeterAisle)
        }),
        comfortTypeId: new FormControl(this.currentProject.comfortTypeId)
      });
      this.changeTransportMode(this.currentProject.transportModeId);
      // set selectedRegion and selectedRegionTransportModeDefault object
      this.changeRegion(this.currentProject.regionId);
    }
  }

  onSubmit() {
    this.skipCanDeactivateOnSave = true;
    if (this.currentAgeGenderDistribution != null) {
      this.setAgeGenderDistribution();
    }
    else {
      if (this.currentProject != null && this.currentProject.ageGenderDistribution != null) {
        this.currentAgeGenderDistribution = this.currentProject.ageGenderDistribution;
        this.setAgeGenderDistribution();
      }
    }

    let project: Project = this.projectForm.getRawValue();
    this.requestService.writeProject(project)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(() => {
        this.navigateBack();
      });
  }

  navigateBack() {
    this.router.navigate(['project/list']);
  }

  changeTransportMode(selectedValue: number) {
    this.selectedTransportMode = this.transportModes.find(r => r.id === selectedValue);
    const reservationCtrl = this.projectForm.get('reservationNeeded');
    this.enableRegionCtrl();

    // special case on update form can't set region transport mode default if selected region isn't set yet!
    if (this.selectedRegion !== undefined) {
      this.setSelectedRegionTransportModeDefault();
    }

    if (selectedValue === TransportModeEnum.LongDistanceTransport) {
      reservationCtrl.enable();
    }
    else {
      reservationCtrl.patchValue(false);
      reservationCtrl.disable();
    }
  }
  enableRegionCtrl() {
    const transportModeCtrl = this.projectForm.get('transportModeId');
    const regionCtrl = this.projectForm.get('regionId');

    if (transportModeCtrl.value !== null) {
      regionCtrl.enable();
    }
    else {
      regionCtrl.disable();
    }
  }

  canDeactivate() {
    if (!this.skipCanDeactivateOnSave) {
      const dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: 'Warning',
          message: this.backButtonModalMessage,
          showCancel: true,
          cancelButtonText: 'Cancel',
          saveButtonText: 'Leave'
        }
      });
      return WaitForModalResult(dialogRef);
    }
    else {
      return true;
    }
  }

  changeRegion(selectedValue: number) {
    this.setSelectedRegion(selectedValue);
    this.setSelectedRegionTransportModeDefault();
  }
  setSelectedRegion(selectedValue: number) {
    this.selectedRegion = this.regions.find(x => x.id == selectedValue);
  }

  setSelectedRegionTransportModeDefault() {
    if (this.selectedRegion != undefined) {
      this.selectedRegionTransportModeDefault = this.cloneService.deepClone(this.selectedRegion.transportModeDefaults.find(x => x.transportModeId === this.selectedTransportMode.id));
    }
  }

  loadproject() {
    this.requestService.getProjectById(this.projectId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe((next: Project) => {
        this.currentProject = next;
        this.updateForm();
      });
  }

  disableKeyPressEnter(event, isKeyEPressable: boolean = false): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    //             enter              e                  E
    if (charCode == 13 || ((charCode == 101 || charCode == 69 || charCode == 45) && !isKeyEPressable)) {
      return false;
    }
  }

  updateValidators(group: FormGroup, formatId: number) {
    const boarding = group.get('personsBoardingDefault');
    const deboarding = group.get('personsDeboardingDefault');

    boarding?.clearValidators();
    deboarding?.clearValidators();

    if (boarding?.value !== null || deboarding?.value !== null) {
      boarding.setValidators([
        Validators.required,
        Validators.min(0),
        ...(formatId === 1 ? [Validators.max(100)] : [])
      ]);

      deboarding.setValidators([
        Validators.required,
        Validators.min(0),
        ...(formatId === 1 ? [Validators.max(100)] : [])
      ]);
    }

    boarding?.updateValueAndValidity({ emitEvent: false });
    deboarding?.updateValueAndValidity({ emitEvent: false });
  }

  onPersonValueFormatClick(newFormatId: number) {
    const defaultGroup = this.projectForm.get('default');
    const currentFormat = defaultGroup.get('personValueFormatId')?.value;

    if (currentFormat !== newFormatId) {
      defaultGroup.get('personValueFormatId')?.patchValue(newFormatId);
      defaultGroup.get('personsBoardingDefault')?.patchValue(null);
      defaultGroup.get('personsDeboardingDefault')?.patchValue(null);
      this.updateValidators(defaultGroup, newFormatId);
    }
  }

  triggerValidatorUpdate() {
    const defaultGroup = this.projectForm.get('default');
    const currentFormat = defaultGroup.get('personValueFormatId')?.value;
    this.updateValidators(defaultGroup, currentFormat);
  }

  openAgeGenderMatrix() {
    const dialogRef = this.dialog.open(AgeGenderCompareDialog, this.getAgeGenderDialogConfig());
    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (this.projectId < 1) {
          if (result && result.result == true) {
            this.currentAgeGenderDistribution = result.data.ageGenderDistribution;
            this.currentAgeGenderDistribution.id = -1;
          }
        }
        else {
          if (result && result.result == true) {
            this.currentAgeGenderDistribution = result.data.ageGenderDistribution;
            if (this.selectedRegion.ageGenderDistribution.id == this.currentAgeGenderDistribution.id) {
              this.currentAgeGenderDistribution.id = -1;
            }
          }
        }
      });
  }

  setAgeGenderDistribution() {
    this.projectForm.get('ageGenderDistribution').patchValue(this.currentAgeGenderDistribution);
  }

  getAgeGenderDialogConfig() {
    let ageGenderDistribution: AgeGenderDistribution = new AgeGenderDistribution();
    let ageGenderDistriFromRegion: AgeGenderDistribution = this.cloneService.deepClone(this.selectedRegion.ageGenderDistribution);
    if (this.projectId < 1) {
      if (this.currentAgeGenderDistribution == null) {
        ageGenderDistribution = this.cloneService.deepClone(this.selectedRegion.ageGenderDistribution);
      }
      else {
        ageGenderDistribution = this.cloneService.deepClone(this.currentAgeGenderDistribution);
      }
    }
    else {
      if (this.currentProject.ageGenderDistribution != null) {
        if (this.currentAgeGenderDistribution == null) {
          ageGenderDistribution = this.cloneService.deepClone(this.currentProject.ageGenderDistribution);
        }
        else {
          ageGenderDistribution = this.cloneService.deepClone(this.currentAgeGenderDistribution);
        }
      }
      else {
        if (this.currentAgeGenderDistribution != null) {
          ageGenderDistribution = this.currentAgeGenderDistribution;
        }
        else {
          ageGenderDistribution = this.cloneService.deepClone(this.selectedRegion.ageGenderDistribution);
        }
      }
    }


    ageGenderDistribution.values = SortAgeGenderDistributionValuesById(ageGenderDistribution.values);

    // config object for the age gender dialog
    let config = {
      width: '1480px',
      data: {
        title: 'Age Gender Distribution',
        showCancel: true,
        cancelButtonText: 'Cancel',
        saveButtonText: 'Save',
        ageGender: ageGenderDistribution,
        height: '150px',
        isEditable: false,
        isCompareMatrixEditable: true,
        isCompareMatrixSumDisplayed: false,
        isSumDisplayed: true,
        ageGenderCompareMatrix: ageGenderDistriFromRegion
      }
    };
    return config;
  }

  ngOnDestroy() {
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
    this.formSubscription?.unsubscribe();
  }
}
